import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import {
  Plus, Edit3, Trash2, Search, X, Users, MessageCircle, UserCog, Send,
  Mic, StopCircle, Upload, Image, Video, Music, Type, ArrowUp, ArrowDown, ChevronDown,
} from 'lucide-react';

const FUNCAO_GESTOR_PADRAO = 'Gestor de Lançamentos';
const FUNCOES_GESTOR = ['Gestor de Lançamentos', 'Gestor Comercial', 'Gestor Administrativo', 'Diretor/Sócio'];

function GestorForm({ initial, onSave, onCancel, saving }) {
  const [nome, setNome] = useState(initial?.nome || '');
  const funcaoInicialEhPadrao = !initial?.funcao || FUNCOES_GESTOR.includes(initial.funcao);
  const [funcao, setFuncao] = useState(funcaoInicialEhPadrao ? (initial?.funcao || FUNCAO_GESTOR_PADRAO) : 'Outro');
  const [funcaoOutro, setFuncaoOutro] = useState(funcaoInicialEhPadrao ? '' : initial.funcao);
  const [whatsapp, setWhatsapp] = useState(initial?.whatsapp || '');
  const [observacoes, setObservacoes] = useState(initial?.observacoes || '');

  const funcaoFinal = funcao === 'Outro' ? funcaoOutro.trim() : funcao;
  const podeSalvar = nome.trim() && funcaoFinal && whatsapp.trim();

  return (
    <div className="space-y-3 border border-gray-100 rounded-lg p-4 bg-gray-50">
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome do gestor *</label><input className="input-field" value={nome} onChange={e => setNome(e.target.value)} autoFocus /></div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Função *</label>
          <select className="input-field" value={funcao} onChange={e => setFuncao(e.target.value)}>
            {FUNCOES_GESTOR.map(f => <option key={f} value={f}>{f}</option>)}
            <option value="Outro">Outro...</option>
          </select>
          {funcao === 'Outro' && (
            <input className="input-field mt-2" placeholder="Especifique a função" value={funcaoOutro} onChange={e => setFuncaoOutro(e.target.value)} />
          )}
        </div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp *</label><input className="input-field" placeholder="DDD + número" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} /></div>
      </div>
      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Observações</label>
        <textarea className="input-field" rows={2} value={observacoes} onChange={e => setObservacoes(e.target.value)} />
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => onSave({ ...(initial?.id ? { id: initial.id } : {}), nome: nome.trim(), funcao: funcaoFinal, whatsapp: whatsapp.trim(), observacoes })}
          disabled={!podeSalvar || saving}
          className="btn-primary disabled:opacity-50"
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
        <button onClick={onCancel} className="btn-secondary">Cancelar</button>
      </div>
    </div>
  );
}

// Gestores da imobiliária — vive dentro do perfil (não faz sentido gerenciar separado, já que
// todo gestor pertence a uma imobiliária específica).
function GestoresSection({ imobiliariaId }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(null); // null | {} (novo) | gestor (editando)

  const { data, isLoading } = useQuery({
    queryKey: ['gestores', imobiliariaId],
    queryFn: () => api.get('/gestores', { params: { imobiliaria_id: imobiliariaId } }).then(r => r.data.data),
  });

  const createMut = useMutation({
    mutationFn: (payload) => api.post('/gestores', { ...payload, imobiliaria_id: imobiliariaId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gestores', imobiliariaId] }); setForm(null); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, ...payload }) => api.put(`/gestores/${id}`, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gestores', imobiliariaId] }); setForm(null); },
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/gestores/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gestores', imobiliariaId] }),
  });

  const salvar = (payload) => (payload.id ? updateMut.mutate(payload) : createMut.mutate(payload));
  const salvando = createMut.isPending || updateMut.isPending;
  const gestores = data || [];

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm text-gray-500 uppercase flex items-center gap-2"><UserCog size={16} /> Gestores ({gestores.length})</h3>
        {!form && <button onClick={() => setForm({})} className="btn-primary flex items-center gap-2 text-sm py-1.5"><Plus size={14} /> Cadastrar gestor</button>}
      </div>

      {form && <div className="mb-4"><GestorForm initial={form} onSave={salvar} onCancel={() => setForm(null)} saving={salvando} /></div>}

      {isLoading ? (
        <div className="text-center text-gray-400 py-6 text-sm">Carregando...</div>
      ) : gestores.length === 0 ? (
        <p className="text-gray-400 text-sm">Nenhum gestor cadastrado.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b"><tr>
            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Nome</th>
            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Função</th>
            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">WhatsApp</th>
            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Observações</th>
            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Ações</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {gestores.map(g => (
              <tr key={g.id}>
                <td className="px-4 py-2">{g.nome}</td>
                <td className="px-4 py-2 text-gray-500">{g.funcao}</td>
                <td className="px-4 py-2 text-gray-500">{g.whatsapp || '—'}</td>
                <td className="px-4 py-2 text-gray-500 max-w-[200px] truncate" title={g.observacoes || ''}>{g.observacoes || '—'}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    {g.whatsapp && (
                      <a
                        href={`https://wa.me/55${g.whatsapp.replace(/\D/g, '')}`}
                        target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs hover:bg-green-100"
                      >
                        <MessageCircle size={13} /> WhatsApp
                      </a>
                    )}
                    <button onClick={() => setForm(g)} className="text-blue-600 hover:text-blue-800"><Edit3 size={14} /></button>
                    <button onClick={() => { if (window.confirm('Remover este gestor?')) deleteMut.mutate(g.id); }} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const CAMPOS_OBRIGATORIOS = [
  ['nome', 'Nome Fantasia'],
  ['razao_social', 'Razão Social'],
  ['cnpj', 'CNPJ'],
  ['email', 'E-mail'],
  ['telefone', 'Telefone'],
  ['endereco', 'Endereço'],
  ['cep', 'CEP'],
  ['cidade', 'Cidade'],
  ['estado', 'Estado'],
  ['creci', 'CRECI'],
  ['responsavel', 'Responsável legal'],
  ['cpf_responsavel', 'CPF do responsável legal'],
];

function ImobiliariaForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || { nome: '', razao_social: '', cnpj: '', creci: '', endereco: '', cep: '', cidade: '', estado: '', telefone: '', email: '', responsavel: '', cpf_responsavel: '', corretores_lancamentos: '', observacoes: '', comissao_percentual: '' });
  const [erro, setErro] = useState('');
  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSave = () => {
    const faltando = CAMPOS_OBRIGATORIOS.filter(([campo]) => !String(form[campo] ?? '').trim()).map(([, label]) => label);
    if (faltando.length > 0) {
      setErro(`Preencha os campos obrigatórios: ${faltando.join(', ')}`);
      return;
    }
    setErro('');
    onSave({ ...form, comissao_percentual: form.comissao_percentual === '' ? null : form.comissao_percentual });
  };

  return (
    <div className="bg-white rounded-xl shadow p-6 space-y-4">
      {erro && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-2 border border-red-200">{erro}</div>}
      <div className="grid grid-cols-2 gap-4">
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome Fantasia *</label><input className="input-field" value={form.nome} onChange={set('nome')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Razão Social *</label><input className="input-field" value={form.razao_social || ''} onChange={set('razao_social')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CNPJ *</label><input className="input-field" value={form.cnpj} onChange={set('cnpj')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CRECI *</label><input className="input-field" value={form.creci} onChange={set('creci')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Responsável legal *</label><input className="input-field" value={form.responsavel} onChange={set('responsavel')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CPF do responsável legal *</label><input className="input-field" value={form.cpf_responsavel || ''} onChange={set('cpf_responsavel')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email *</label><input className="input-field" value={form.email} onChange={set('email')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Telefone *</label><input className="input-field" value={form.telefone} onChange={set('telefone')} required /></div>
        <div className="col-span-2"><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Endereço *</label><input className="input-field" value={form.endereco} onChange={set('endereco')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CEP *</label><input className="input-field" value={form.cep || ''} onChange={set('cep')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cidade *</label><input className="input-field" value={form.cidade || ''} onChange={set('cidade')} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Estado *</label><input className="input-field" value={form.estado || ''} onChange={set('estado')} maxLength={2} required /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nº de corretores envolvidos com lançamentos</label><input type="number" min="0" className="input-field" value={form.corretores_lancamentos ?? ''} onChange={set('corretores_lancamentos')} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">% de comissão padrão</label><input type="number" step="0.01" min="0" max="100" className="input-field" value={form.comissao_percentual ?? ''} onChange={set('comissao_percentual')} placeholder="Ex: 5 (vale pra todos os corretores dela, salvo override individual)" /></div>
      </div>
      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Observações</label>
        <textarea className="input-field" rows={3} value={form.observacoes || ''} onChange={set('observacoes')} />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={handleSave} className="btn-primary">Salvar</button>
        <button onClick={onCancel} className="btn-secondary">Cancelar</button>
      </div>
    </div>
  );
}

const SHORTCODES_DISPARO = [
  { code: '#primeironome', desc: 'Primeiro nome do gestor' },
  { code: '#nomecompleto', desc: 'Nome completo' },
  { code: '#periodododia', desc: 'Bom dia / Boa tarde / Boa noite' },
  { code: '#diasemana', desc: 'Ex: Segunda-feira' },
  { code: '#dia', desc: 'Dia do mês' },
  { code: '#mes', desc: 'Nome do mês' },
];

function ShortcodeSelectorDisparo({ onInsert }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium hover:bg-blue-100">
        <span>#</span> Atalhos <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-56">
          {SHORTCODES_DISPARO.map(s => (
            <button key={s.code} type="button" onClick={() => { onInsert(s.code); setOpen(false); }} className="w-full text-left px-3 py-1.5 hover:bg-blue-50 flex items-center gap-2">
              <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{s.code}</span>
              <span className="text-xs text-gray-500">{s.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AudioRecorderDisparo({ onAudioReady }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mr = useRef(null); const chunks = useRef([]); const timer = useRef(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunks.current = [];
      rec.ondataavailable = e => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = () => { const blob = new Blob(chunks.current, { type: 'audio/webm' }); onAudioReady({ arquivo: blob, preview: URL.createObjectURL(blob), nomeOriginal: `audio_${Date.now()}.webm` }); stream.getTracks().forEach(t => t.stop()); };
      rec.start(); mr.current = rec; setRecording(true); setSeconds(0);
      timer.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch { alert('Microfone indisponível.'); }
  };
  const stop = () => { mr.current?.stop(); clearInterval(timer.current); setRecording(false); };
  return recording
    ? <button type="button" onClick={stop} className="flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600"><StopCircle size={13} /> Parar ({seconds}s)</button>
    : <button type="button" onClick={start} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200"><Mic size={13} /> Gravar PTT</button>;
}

const STEP_LABELS_DISPARO = { texto: 'Texto', imagem: 'Imagem', video: 'Vídeo', audio: 'Áudio' };
const STEP_ICONS_DISPARO = { texto: Type, imagem: Image, video: Video, audio: Music };

function DisparoStepCard({ step, index, total, onUpdate, onRemove, onMoveUp, onMoveDown }) {
  const fileRef = useRef(null);
  const textRef = useRef(null);
  const Icon = STEP_ICONS_DISPARO[step.tipo];
  const handleFile = (e) => { const file = e.target.files[0]; if (!file) return; onUpdate({ ...step, arquivo: file, preview: URL.createObjectURL(file), nomeOriginal: file.name }); e.target.value = ''; };
  const insertShortcode = (code) => {
    const ta = textRef.current;
    if (!ta) { onUpdate({ ...step, conteudo: (step.conteudo || '') + code }); return; }
    const start = ta.selectionStart; const end = ta.selectionEnd;
    const text = step.conteudo || '';
    const newText = text.substring(0, start) + code + text.substring(end);
    onUpdate({ ...step, conteudo: newText });
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + code.length; }, 0);
  };
  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-white space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-lg">
          <Icon size={13} className="text-gray-600" />
          <span className="text-xs font-semibold text-gray-600">{index + 1}. {STEP_LABELS_DISPARO[step.tipo]}</span>
        </div>
        <div className="flex-1" />
        <button type="button" onClick={onMoveUp} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp size={14} /></button>
        <button type="button" onClick={onMoveDown} disabled={index === total - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown size={14} /></button>
        <button type="button" onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
      </div>
      {step.tipo === 'texto' && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <ShortcodeSelectorDisparo onInsert={insertShortcode} />
            <span className="text-xs text-gray-400">Use # para personalizar</span>
          </div>
          <textarea ref={textRef} value={step.conteudo || ''} onChange={e => onUpdate({ ...step, conteudo: e.target.value })} placeholder="Digite a mensagem..." rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y" />
        </div>
      )}
      {step.tipo === 'imagem' && (<div>
        {step.preview ? (<div className="flex items-center gap-3"><img src={step.preview} alt="" className="h-16 w-16 object-cover rounded-lg border" /><span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span><button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>)
        : (<button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500"><Image size={20} className="mx-auto mb-1 opacity-50" />Selecionar imagem</button>)}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>)}
      {step.tipo === 'video' && (<div>
        {step.preview ? (<div className="flex items-center gap-3"><video src={step.preview} className="h-16 w-24 object-cover rounded-lg border" /><span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span><button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>)
        : (<button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500"><Video size={20} className="mx-auto mb-1 opacity-50" />Selecionar vídeo</button>)}
        <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/*" className="hidden" onChange={handleFile} />
      </div>)}
      {step.tipo === 'audio' && (<div>
        {step.preview ? (<div className="flex items-center gap-3"><audio src={step.preview} controls className="h-8 flex-1" /><span className="text-xs text-gray-500 truncate max-w-[120px]">{step.nomeOriginal}</span><button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>)
        : (<div className="flex items-center gap-2"><button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200"><Upload size={13} /> Arquivo</button><AudioRecorderDisparo onAudioReady={a => onUpdate({ ...step, ...a })} /></div>)}
        <input ref={fileRef} type="file" accept=".mp3,.ogg,.opus,.wav,.m4a,.webm" className="hidden" onChange={handleFile} />
      </div>)}
    </div>
  );
}

// Disparo em massa pra lista de gestores das imobiliárias — mesma infra de campanha
// (POST /envios-massa, backend já suporta esteira multi-tipo com upload de arquivo por step e
// substitui #shortcodes por contato no envio) usada pelo Funil de Leads Corretores, só que aqui
// com esteira completa (texto/imagem/vídeo/áudio) em vez de só texto.
function DisparoGestoresModal({ isOpen, onClose, gestores }) {
  const qc = useQueryClient();
  const [selecionados, setSelecionados] = useState(new Set());
  const [steps, setSteps] = useState([]);
  const [instancia, setInstancia] = useState('');
  const [delaySeg, setDelaySeg] = useState(5);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const gestoresComWhats = gestores.filter(g => g.whatsapp);

  const { data: instanciasData } = useQuery({
    queryKey: ['evolution-instancias-gestores'],
    queryFn: () => api.get('/envios-massa/instancias').then(r => r.data),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });
  const instancias = instanciasData?.data || [];

  useEffect(() => { if (instancias.length && !instancia) setInstancia(instancias[0].name); }, [instancias, instancia]);
  useEffect(() => {
    if (isOpen) setSelecionados(new Set(gestores.filter(g => g.whatsapp).map(g => g.id)));
  }, [isOpen, gestores]);

  const toggleGestor = (id) => {
    setSelecionados(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const marcarTodos = () => setSelecionados(new Set(gestoresComWhats.map(g => g.id)));
  const desmarcarTodos = () => setSelecionados(new Set());

  const addStep = (tipo) => setSteps(prev => [...prev, { tipo, conteudo: '', arquivo: null, preview: null, nomeOriginal: '', legenda: '' }]);
  const updateStep = (idx, step) => setSteps(prev => prev.map((s, i) => i === idx ? step : s));
  const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));
  const moveStep = (idx, dir) => { const arr = [...steps]; const t = idx + dir; if (t < 0 || t >= arr.length) return; [arr[idx], arr[t]] = [arr[t], arr[idx]]; setSteps(arr); };

  const reset = () => { setSteps([]); setError(''); };
  const handleClose = () => { if (!sending) { reset(); onClose(); } };

  const contatos = gestoresComWhats
    .filter(g => selecionados.has(g.id))
    .map(g => ({ nome: g.nome, telefone: g.whatsapp }));

  const handleSubmit = async () => {
    setError('');
    if (!instancia) return setError('Selecione a instância WhatsApp');
    if (!contatos.length) return setError('Selecione ao menos um gestor');
    if (steps.length === 0) return setError('Adicione ao menos um bloco na esteira');
    const hasContent = steps.some(s => s.tipo === 'texto' ? s.conteudo.trim() : s.arquivo);
    if (!hasContent) return setError('Preencha o conteúdo dos blocos da esteira');

    setSending(true);
    try {
      const fd = new FormData();
      const stepsConfig = steps.map(s => s.tipo === 'texto' ? { tipo: 'texto', conteudo: s.conteudo } : { tipo: s.tipo, legenda: s.legenda || '', nomeOriginal: s.nomeOriginal || '' });
      fd.append('config', JSON.stringify({
        nome: `Gestores imobiliárias - ${new Date().toLocaleDateString('pt-BR')}`,
        instancia_evolution: instancia,
        instancia_tipo: 'evolution',
        delay_segundos: Number(delaySeg) || 5,
        contatos,
        steps: stepsConfig,
        autostart: true,
        origem: 'gestores_imobiliarias',
      }));
      steps.forEach((s, i) => { if (s.tipo !== 'texto' && s.arquivo) fd.append(`step_${i}`, s.arquivo, s.nomeOriginal || `step_${i}`); });
      await api.post('/envios-massa', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      qc.invalidateQueries({ queryKey: ['campanhas-massa'] });
      reset();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-800">Mensagens em massa — Gestores das Imobiliárias</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto p-6 flex-1 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Destinatários ({contatos.length} de {gestoresComWhats.length})</label>
              <div className="flex gap-2">
                <button type="button" onClick={marcarTodos} className="text-xs text-primary hover:underline">Marcar todos</button>
                <button type="button" onClick={desmarcarTodos} className="text-xs text-gray-500 hover:underline">Desmarcar todos</button>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
              {gestoresComWhats.length === 0 && <div className="p-3 text-sm text-gray-400">Nenhum gestor com WhatsApp cadastrado.</div>}
              {gestoresComWhats.map(g => (
                <label key={g.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={selecionados.has(g.id)} onChange={() => toggleGestor(g.id)} />
                  <span className="font-medium">{g.nome}</span>
                  <span className="text-gray-400">— {g.imobiliaria_nome}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Instância WhatsApp *</label>
              {instancias.length === 0
                ? <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">Nenhuma instância conectada.</div>
                : <select value={instancia} onChange={e => setInstancia(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">{instancias.map(i => <option key={i.name} value={i.name}>{i.name} — {i.profileName || ''}</option>)}</select>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Intervalo entre envios (segundos)</label>
              <input type="number" min="2" value={delaySeg} onChange={e => setDelaySeg(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700 text-sm">Esteira de Mensagens</h3>
              <span className="text-xs text-gray-400">{steps.length} bloco(s)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => addStep('texto')} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100"><Type size={13} /> + Texto</button>
              <button type="button" onClick={() => addStep('imagem')} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100"><Image size={13} /> + Imagem</button>
              <button type="button" onClick={() => addStep('video')} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100"><Video size={13} /> + Vídeo</button>
              <button type="button" onClick={() => addStep('audio')} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium hover:bg-orange-100"><Music size={13} /> + Áudio</button>
            </div>
            {steps.length > 0 ? (
              <div className="space-y-2">
                {steps.map((step, i) => <DisparoStepCard key={i} step={step} index={i} total={steps.length} onUpdate={s => updateStep(i, s)} onRemove={() => removeStep(i)} onMoveUp={() => moveStep(i, -1)} onMoveDown={() => moveStep(i, 1)} />)}
              </div>
            ) : <div className="py-6 text-center text-gray-300 text-sm">Nenhum bloco adicionado.</div>}
          </div>
        </div>
        <div className="p-6 border-t">
          <button onClick={handleSubmit} disabled={sending} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 disabled:opacity-50 shadow">
            <Send size={16} /> {sending ? 'Disparando...' : `Disparar para ${contatos.length} gestor(es)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Lista global de gestores (todas as imobiliárias) — só leitura + botão de disparo em massa,
// o CRUD por gestor continua no perfil de cada imobiliária (GestoresSection acima).
function GestoresListaTab() {
  const [search, setSearch] = useState('');
  const [disparoOpen, setDisparoOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['gestores-todos', search],
    queryFn: () => api.get('/gestores', { params: { search } }).then(r => r.data.data),
  });
  const gestores = data || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-3 text-gray-400" />
          <input className="input-field pl-10" placeholder="Buscar por gestor ou imobiliária..." value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-3 text-gray-400"><X size={16} /></button>}
        </div>
        <button onClick={() => setDisparoOpen(true)} className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 whitespace-nowrap">
          <Send size={15} /> Mensagens em massa
        </button>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Carregando...</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Imobiliária</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Gestor</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Função</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">WhatsApp</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Observações</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {gestores.map(g => (
                <tr key={g.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{g.imobiliaria_nome}</td>
                  <td className="px-4 py-3 text-gray-500">{g.nome}</td>
                  <td className="px-4 py-3 text-gray-500">{g.funcao}</td>
                  <td className="px-4 py-3 text-gray-500">{g.whatsapp || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 max-w-[240px] truncate" title={g.observacoes || ''}>{g.observacoes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {gestores.length === 0 && <div className="text-center text-gray-400 py-8">Nenhum gestor cadastrado.</div>}
        </div>
      )}

      <DisparoGestoresModal isOpen={disparoOpen} onClose={() => setDisparoOpen(false)} gestores={gestores} />
    </div>
  );
}

export default function ImobiliariasList() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('imobiliarias');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ['imobiliarias', search], queryFn: () => api.get('/imobiliarias', { params: { search } }).then(r => r.data.data) });
  const createMut = useMutation({ mutationFn: (d) => api.post('/imobiliarias', d), onSuccess: () => { qc.invalidateQueries(['imobiliarias']); setCreating(false); } });
  const updateMut = useMutation({ mutationFn: ({ id, ...d }) => api.put(`/imobiliarias/${id}`, d), onSuccess: () => { qc.invalidateQueries(['imobiliarias']); setEditing(null); } });
  const deleteMut = useMutation({ mutationFn: (id) => api.delete(`/imobiliarias/${id}`), onSuccess: () => qc.invalidateQueries(['imobiliarias']) });

  const { data: detailData } = useQuery({ queryKey: ['imobiliaria', detail], queryFn: () => api.get(`/imobiliarias/${detail}`).then(r => r.data.data), enabled: !!detail });

  if (creating) return (<div><div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold">Nova Imobiliária</h1></div><ImobiliariaForm onSave={(d) => createMut.mutate(d)} onCancel={() => setCreating(false)} /></div>);
  if (editing) return (<div><div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold">Editar Imobiliária</h1></div><ImobiliariaForm initial={editing} onSave={(d) => updateMut.mutate({ id: editing.id, ...d })} onCancel={() => setEditing(null)} /></div>);

  if (detail && detailData) return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setDetail(null)} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
        <h1 className="text-xl font-bold">{detailData.nome}</h1>
      </div>
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow p-6 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Razão Social</span><span>{detailData.razao_social || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">CNPJ</span><span>{detailData.cnpj || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">CRECI</span><span>{detailData.creci || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Responsável legal</span><span>{detailData.responsavel || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">CPF do responsável legal</span><span>{detailData.cpf_responsavel || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Email</span><span>{detailData.email || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Telefone</span><span>{detailData.telefone || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Endereço</span><span>{detailData.endereco || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">CEP</span><span>{detailData.cep || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Cidade/Estado</span><span>{detailData.cidade ? `${detailData.cidade}/${detailData.estado || '—'}` : '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Nº corretores em lançamentos</span><span>{detailData.corretores_lancamentos ?? '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">% de comissão padrão</span><span>{detailData.comissao_percentual != null ? `${detailData.comissao_percentual}%` : '—'}</span></div>
        </div>
        <div className="bg-white rounded-xl shadow p-6 text-sm">
          <span className="text-gray-500 block mb-1">Observações</span>
          <p className="whitespace-pre-wrap">{detailData.observacoes || '—'}</p>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h3 className="font-bold text-sm text-gray-500 uppercase mb-4 flex items-center gap-2"><Users size={16} /> Corretores Vinculados ({(detailData.corretores || []).length})</h3>
        {(detailData.corretores || []).length === 0 ? <p className="text-gray-400 text-sm">Nenhum corretor vinculado.</p> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Nome</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">CRECI</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">WhatsApp</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {detailData.corretores.map(c => (
                <tr key={c.id}><td className="px-4 py-2">{c.nome}</td><td className="px-4 py-2 text-gray-500">{c.creci}</td><td className="px-4 py-2 text-gray-500">{c.whatsapp}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <GestoresSection imobiliariaId={detail} />
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-800">Imobiliárias</h1>
        {tab === 'imobiliarias' && (
          <button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-2"><Plus size={16} /> Nova Imobiliária</button>
        )}
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-4">
        {[['imobiliarias', 'Imobiliárias'], ['gestores', 'Lista de Gestores']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'gestores' ? <GestoresListaTab /> : (
      <>
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-3 text-gray-400" />
        <input className="input-field pl-10" placeholder="Buscar por nome..." value={search} onChange={e => setSearch(e.target.value)} />
        {search && <button onClick={() => setSearch('')} className="absolute right-3 top-3 text-gray-400"><X size={16} /></button>}
      </div>
      {isLoading ? <div className="text-center text-gray-400 py-12">Carregando...</div> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Nome</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">CNPJ</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">CRECI</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Responsável</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {(data || []).filter(i => i.ativa !== false).map(i => (
                <tr key={i.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium cursor-pointer text-blue-600 hover:underline" onClick={() => setDetail(i.id)}>{i.nome}</td>
                  <td className="px-4 py-3 text-gray-500">{i.cnpj || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{i.creci || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{i.responsavel || '—'}</td>
                  <td className="px-4 py-3"><div className="flex gap-2">
                    <button onClick={() => setEditing(i)} className="text-blue-600 hover:text-blue-800"><Edit3 size={15} /></button>
                    <button onClick={() => { if (window.confirm('Desativar esta imobiliária?')) deleteMut.mutate(i.id); }} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data || []).filter(i => i.ativa !== false).length === 0 && <div className="text-center text-gray-400 py-8">Nenhuma imobiliária encontrada.</div>}
        </div>
      )}
      </>
      )}
    </div>
  );
}
